import React, { useCallback, useEffect, useMemo, useState, } from 'react';

import { useTranslation, } from 'react-i18next';
import { useNavigate, } from 'react-router-dom';
import {
  MailAccountAddForm,
  MailAccountAddInitialValues,
  MailAccountComponents,
  MailAccountAdditionalParams,
  MailAccountFormOptions,
  onMailAccountFormSaved,
  IMailAccountFormOptionsProps,
  AlertError,
  queryParam,
} from '@Utils';
import { AtlasForm, IFormOptions, } from 'atlas-form';
import { CrudEnum, ISharedCreateMailAccount, PermissionEnum, } from 'atlas-shared';
import { createMailAccount, fetchMailAccount, getMailAccountCreateSchema, } from '@Api';
import { RuleProtected, } from '@Containers';
import { withAdd, IWithAddProps, } from '@Hocs/with.add';
import { useAbilities, useAuth, useOrganizations, useJourneies, } from '@Hooks';
import { AppSpinner, } from '@Components';
import { useAppDispatch, } from '@Store';

interface IProps extends IWithAddProps<ISharedCreateMailAccount> {}

const MailAccountFormAdd = ({ values, setValues, save, formSchema, schemaLoading, saving, initialValues, title, onSaved, onCancel, onChange, hasAdvanced = false, }: IProps) => {
  const organizations = useOrganizations();
  const journeies = useJourneies();
  const abilities = useAbilities();
  const { t, } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const dispatch = useAppDispatch();

  const [cloned, setCloned, ] = useState<Partial<ISharedCreateMailAccount>>();

  const param_props: IMailAccountFormOptionsProps = {
    t,
    is_new: true,
    abilities,
    organizations: organizations.organizations,
    journeies: journeies.journeies,
  };
  const formLayout = useMemo(() => MailAccountAddForm(values, param_props, t), [values, organizations, journeies, t, abilities, ]);
  const options: IFormOptions = useMemo(() => MailAccountFormOptions(values || MailAccountAddInitialValues, param_props, t), [values, organizations, journeies, t, abilities, ]);
  
  const onValuesChange = useCallback((changedValue: any, values: ISharedCreateMailAccount, form) => {
    setValues(values);
    onChange?.(values, changedValue, form);
  }, [setValues, ]);
  const onFinish = useCallback((values: ISharedCreateMailAccount) => save(() => createMailAccount(values).then(
    res => onSaved ? onSaved(res, navigate) : onMailAccountFormSaved(t, 'add', res, navigate),
    err => {
      AlertError(t, {
        content: err,
      });
    }
  )), [save, navigate, t, ]);
  
  useEffect(() => {
    const id = queryParam('clone');

    if (id)
      fetchMailAccount(+id)
        .then(({
          organization_id,
          title,
          address,
          inbound_journey_id,
          outbound_journey_id,
          active,
          imap,
          smtp,
        }) => {
          setCloned({
            organization_id,
            title,
            address,
            inbound_journey_id,
            outbound_journey_id,
            active,
            imap,
            smtp,
          });
        });
    else
      setCloned({});
  }, []);
  
  if (schemaLoading || !cloned)
    return <AppSpinner />;

  if (!formSchema)
    return <>{t('SCHEMA_NOT_FOUND')}</>;
  
  if (!organizations.loaded || !journeies.loaded)
    return <>{t('LOADING_DEPENDENCIES')}</>;

  return (
    <RuleProtected
      isAllowed={() => !values || abilities.can(values.organization_id, CrudEnum.Insert, PermissionEnum.MailAccount, values)}
    >
      <AtlasForm
        dispatch={dispatch}
        formLayout={formLayout}
        form={formSchema}
        options={options}
        components={MailAccountComponents}
        additionalParams={MailAccountAdditionalParams(auth, param_props)}
        onFinish={onFinish}
        onCancel={onCancel}
        onValuesChange={onValuesChange}
        initialValues={{ ...(cloned || {}), ...MailAccountAddInitialValues, ...initialValues, }}
        saving={saving}
        title={title ? t(title) : `${t('ADD')} ${t('MAIL_ACCOUNT')}`}
        hasAdvanced={hasAdvanced}
      />
    </RuleProtected>
  );
};

export const MailAccountFormAddView = React.memo(withAdd(MailAccountFormAdd, getMailAccountCreateSchema, MailAccountAddInitialValues));
